/**
 * BrightDocs MUI Theme
 * ===================
 * Visual identity: "warm paper + forest teal"
 *   - Page background is warm paper (#F6F5F0); cards are white with a 1px
 *     divider border and no shadow (hover lift only on clickable rows).
 *   - One deep accent (forest teal #0F6B5F) used sparingly; warm amber as the
 *     attention colour.
 *   - Big editorial page titles (Manrope 800) with a small-caps "eyebrow" label
 *     above them; Inter for body copy; Source Serif 4 reserved for document
 *     content (see `docSerif`).
 *   - Status is shown as short WORDS on tinted pills (see shared/StatusPill).
 *   - WCAG 2.1 AA contrast on all text/background pairs below.
 */

import type { CSSProperties } from 'react';
import { createTheme, alpha } from '@mui/material/styles';

// ---------------------------------------------------------------------------
// Module augmentation: `eyebrow` typography variant + `docSerif` font token.
// ---------------------------------------------------------------------------
declare module '@mui/material/styles' {
  interface TypographyVariants {
    eyebrow: CSSProperties;
    /** Serif stack reserved for document content (extracted text, previews). */
    docSerif: string;
  }
  interface TypographyVariantsOptions {
    eyebrow?: CSSProperties;
    docSerif?: string;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    eyebrow: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipClasses {
    /**
     * Slot MUI's Chip overridesResolver reads last for variant="filled" +
     * color="default" (`styles[`${variant}${Color}`]`) but does not declare in
     * its class list. Declared here so the theme override type-checks.
     */
    filledDefault: string;
  }
}

// ---------------------------------------------------------------------------
// Design tokens
// ---------------------------------------------------------------------------

/** Raw colour tokens. Prefer palette keys in components; these exist for
 *  charts, gradients and the layout shells. */
export const tokens = {
  paper: '#F6F5F0',
  card: '#FFFFFF',
  ink: '#14201B',
  inkSecondary: '#5B6660',
  inkDisabled: '#9AA39E',
  divider: '#E6E3DA',
  accent: '#0F6B5F',
  accentDark: '#0A5248',
  accentTint: '#E3F1EE',
  amber: '#B8741A',
  amberTint: '#FBF1E3',
  success: '#1F7A4D',
  successTint: '#E6F4EC',
  error: '#B42318',
  errorTint: '#FBEAE8',
  warning: '#8A5C00', // 5.31:1 on warningTint (AA for 11-12px bold pill text)
  warningTint: '#FFF4D6',
  info: '#2B5FB3',
  infoTint: '#E8EFFA',
} as const;

/** Font stacks. */
export const fonts = {
  heading: '"Manrope", "Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  body: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  serif: '"Source Serif 4", "Source Serif Pro", Georgia, "Times New Roman", serif',
  mono: '"JetBrains Mono", "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
} as const;

/** Serif stack for document content — also exposed as theme.typography.docSerif. */
export const docSerif = fonts.serif;

/** Status tones: ink colour + tint background for pills, alerts and badges. */
export type Tone = 'success' | 'info' | 'error' | 'warning' | 'neutral' | 'accent';
export const toneColors: Record<Tone, { fg: string; bg: string; border: string }> = {
  success: { fg: tokens.success, bg: tokens.successTint, border: alpha(tokens.success, 0.18) },
  info: { fg: tokens.info, bg: tokens.infoTint, border: alpha(tokens.info, 0.18) },
  error: { fg: tokens.error, bg: tokens.errorTint, border: alpha(tokens.error, 0.18) },
  warning: { fg: tokens.warning, bg: tokens.warningTint, border: alpha(tokens.warning, 0.2) },
  neutral: { fg: tokens.inkSecondary, bg: '#EEEDE7', border: tokens.divider },
  accent: { fg: tokens.accent, bg: tokens.accentTint, border: alpha(tokens.accent, 0.18) },
};

/** Brand tokens kept for backwards compatibility with older call sites. */
export const brand = {
  teal: tokens.accent,
  tealDark: tokens.accentDark,
  tealLight: '#2E8F81',
  tealFaint: tokens.accentTint,
  amber: tokens.amber,
  slate900: tokens.ink,
  slate500: tokens.inkSecondary,
  slate200: tokens.divider,
};

/** Light paper sidebar palette used by AdminLayout + PortalLayout. */
export const sidebar = {
  bg: tokens.paper,
  bgPaper: tokens.paper,
  text: tokens.inkSecondary,
  textActive: tokens.accent,
  heading: tokens.inkSecondary,
  active: tokens.accentTint,
  hover: alpha(tokens.ink, 0.05),
  divider: tokens.divider,
};

/** Eyebrow label style (small caps above page titles, table heads, card kickers). */
export const eyebrowStyle = {
  fontFamily: fonts.heading,
  fontSize: '0.75rem',
  fontWeight: 800,
  letterSpacing: '0.14em',
  textTransform: 'uppercase' as const,
  lineHeight: 1.4,
  color: tokens.inkSecondary,
};

// ---------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: tokens.accent,
      light: tokens.accentTint,
      dark: tokens.accentDark,
      contrastText: '#ffffff',
    },
    secondary: {
      main: tokens.amber,
      light: tokens.amberTint,
      dark: '#8C5611',
      contrastText: '#ffffff',
    },
    error: {
      main: tokens.error,
      light: tokens.errorTint,
      dark: '#8E1B12',
      contrastText: '#ffffff',
    },
    warning: {
      main: tokens.warning,
      light: tokens.warningTint,
      dark: '#7A5200',
      contrastText: '#ffffff',
    },
    info: {
      main: tokens.info,
      light: tokens.infoTint,
      dark: '#214B8F',
      contrastText: '#ffffff',
    },
    success: {
      main: tokens.success,
      light: tokens.successTint,
      dark: '#175F3C',
      contrastText: '#ffffff',
    },
    background: {
      default: tokens.paper,
      paper: tokens.card,
    },
    text: {
      primary: tokens.ink,
      secondary: tokens.inkSecondary,
      disabled: tokens.inkDisabled,
    },
    divider: tokens.divider,
    action: {
      hover: alpha(tokens.ink, 0.04),
      selected: alpha(tokens.accent, 0.1),
      focus: alpha(tokens.accent, 0.16),
    },
  },
  typography: {
    fontFamily: fonts.body,
    docSerif: fonts.serif,
    h1: {
      fontFamily: fonts.heading,
      fontSize: '2.5rem',
      fontWeight: 800,
      letterSpacing: '-0.02em',
      lineHeight: 1.15,
    },
    h2: {
      fontFamily: fonts.heading,
      fontSize: '1.625rem',
      fontWeight: 800,
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
    },
    h3: {
      fontFamily: fonts.heading,
      fontSize: '1.375rem',
      fontWeight: 800,
      letterSpacing: '-0.015em',
      lineHeight: 1.25,
    },
    h4: {
      fontFamily: fonts.heading,
      fontSize: '1.875rem',
      fontWeight: 800,
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
    },
    h5: {
      fontFamily: fonts.heading,
      fontSize: '1.25rem',
      fontWeight: 800,
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
    },
    h6: {
      fontFamily: fonts.heading,
      fontSize: '1.0625rem',
      fontWeight: 700,
      letterSpacing: '-0.01em',
      lineHeight: 1.35,
    },
    subtitle1: { fontWeight: 600, fontSize: '1rem', lineHeight: 1.5 },
    subtitle2: { fontWeight: 700, fontSize: '0.9375rem', lineHeight: 1.45 },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
    body2: { fontSize: '0.9063rem', lineHeight: 1.55 },
    caption: { fontSize: '0.8125rem', lineHeight: 1.45 },
    overline: {
      fontFamily: fonts.heading,
      fontSize: '0.75rem',
      fontWeight: 800,
      letterSpacing: '0.14em',
      textTransform: 'uppercase' as const,
      lineHeight: 1.4,
    },
    eyebrow: eyebrowStyle,
    button: {
      fontFamily: fonts.heading,
      textTransform: 'none' as const,
      fontWeight: 700,
      letterSpacing: '-0.005em',
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: tokens.paper,
          color: tokens.ink,
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: 8, height: 8 },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: 4,
            backgroundColor: '#CFCCC2',
          },
          textRendering: 'optimizeLegibility',
          WebkitFontSmoothing: 'antialiased',
        },
        // Document content opts into the serif via a class, no JS required.
        '.bd-doc-serif': {
          fontFamily: fonts.serif,
        },
        // Skip-to-content link for accessibility
        '#skip-to-content': {
          position: 'absolute',
          left: -9999,
          top: 'auto',
          width: 1,
          height: 1,
          overflow: 'hidden',
          '&:focus': {
            position: 'fixed',
            top: 8,
            left: 8,
            width: 'auto',
            height: 'auto',
            padding: '8px 16px',
            backgroundColor: tokens.accent,
            color: '#ffffff',
            borderRadius: 10,
            zIndex: 9999,
            fontSize: '0.875rem',
            fontWeight: 700,
          },
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          eyebrow: 'span',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none' as const,
          fontWeight: 700,
          fontSize: '0.9375rem',
          paddingTop: 7,
          paddingBottom: 7,
          paddingLeft: 16,
          paddingRight: 16,
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: tokens.accentDark,
          },
        },
        outlined: {
          borderColor: tokens.divider,
          backgroundColor: tokens.card,
          color: tokens.ink,
          '&:hover': {
            borderColor: alpha(tokens.ink, 0.3),
            backgroundColor: tokens.paper,
          },
        },
        outlinedPrimary: {
          borderColor: alpha(tokens.accent, 0.4),
          color: tokens.accent,
          '&:hover': {
            borderColor: tokens.accent,
            backgroundColor: tokens.accentTint,
          },
        },
        text: {
          paddingLeft: 10,
          paddingRight: 10,
        },
        sizeSmall: {
          fontSize: '0.875rem',
          paddingTop: 4,
          paddingBottom: 4,
          paddingLeft: 12,
          paddingRight: 12,
        },
        sizeLarge: {
          fontSize: '1rem',
          paddingTop: 10,
          paddingBottom: 10,
          paddingLeft: 20,
          paddingRight: 20,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: 10,
        },
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
          border: `1px solid ${tokens.divider}`,
          boxShadow: 'none',
          backgroundColor: tokens.card,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 20,
          '&:last-child': { paddingBottom: 20 },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: fonts.heading,
          fontWeight: 700,
          fontSize: '0.8125rem',
          borderRadius: 999,
          height: 28,
        },
        label: {
          paddingLeft: 10,
          paddingRight: 10,
        },
        sizeSmall: {
          height: 24,
          fontSize: '0.75rem',
        },
        labelSmall: {
          paddingLeft: 8,
          paddingRight: 8,
        },
        // Neutral pill for filled + color="default" only. A plain `filled`
        // slot resolves after colorPrimary/colorSuccess/... and would clobber
        // every semantic tint; `filledDefault` matches only the default colour.
        filledDefault: {
          backgroundColor: '#EEEDE7',
          color: tokens.ink,
        },
        outlined: {
          borderColor: tokens.divider,
          backgroundColor: tokens.card,
        },
        colorPrimary: {
          backgroundColor: tokens.accentTint,
          color: tokens.accent,
        },
        colorSecondary: {
          backgroundColor: tokens.amberTint,
          color: tokens.amber,
        },
        colorSuccess: {
          backgroundColor: tokens.successTint,
          color: tokens.success,
        },
        colorError: {
          backgroundColor: tokens.errorTint,
          color: tokens.error,
        },
        colorWarning: {
          backgroundColor: tokens.warningTint,
          color: tokens.warning,
        },
        colorInfo: {
          backgroundColor: tokens.infoTint,
          color: tokens.info,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.card,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            ...eyebrowStyle,
            backgroundColor: tokens.card,
            borderBottom: `1px solid ${tokens.divider}`,
            paddingTop: 10,
            paddingBottom: 10,
          },
          '& .MuiTableSortLabel-root': {
            ...eyebrowStyle,
          },
          '& .MuiTableSortLabel-root.Mui-active': {
            color: tokens.ink,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${tokens.divider}`,
          paddingTop: 12,
          paddingBottom: 12,
        },
        body: {
          fontSize: '0.9063rem',
          color: tokens.ink,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child td': { borderBottom: 0 },
          '&.MuiTableRow-hover:hover': {
            backgroundColor: alpha(tokens.accent, 0.035),
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.card,
        },
        toolbar: {
          minHeight: 48,
        },
        selectLabel: { fontSize: '0.875rem', color: tokens.inkSecondary },
        displayedRows: { fontSize: '0.875rem', color: tokens.inkSecondary },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: tokens.paper,
          borderRight: `1px solid ${tokens.divider}`,
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundColor: tokens.paper,
          color: tokens.ink,
          boxShadow: 'none',
          borderBottom: `1px solid ${tokens.divider}`,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 56,
          '@media (min-width:600px)': { minHeight: 56 },
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
        enterDelay: 400,
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: tokens.ink,
          color: '#F6F5F0',
          fontSize: '0.8125rem',
          fontWeight: 500,
          borderRadius: 8,
          padding: '6px 10px',
        },
        arrow: {
          color: tokens.ink,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
        variant: 'outlined',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.card,
          borderRadius: 10,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.divider,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(tokens.ink, 0.3),
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.accent,
            borderWidth: 1.5,
          },
        },
        input: {
          fontSize: '0.9375rem',
        },
        inputSizeSmall: {
          paddingTop: 8,
          paddingBottom: 8,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.9375rem',
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          border: `1px solid ${tokens.divider}`,
          boxShadow: '0 12px 32px -12px rgba(20, 32, 27, 0.22)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.9375rem',
          borderRadius: 6,
          marginLeft: 4,
          marginRight: 4,
          '&.Mui-selected': {
            backgroundColor: tokens.accentTint,
            '&:hover': { backgroundColor: tokens.accentTint },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          border: `1px solid ${tokens.divider}`,
          boxShadow: '0 24px 64px -24px rgba(20, 32, 27, 0.35)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: fonts.heading,
          fontSize: '1.25rem',
          fontWeight: 800,
          letterSpacing: '-0.015em',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontSize: '0.9375rem',
          alignItems: 'center',
          border: '1px solid transparent',
        },
        standardSuccess: {
          backgroundColor: tokens.successTint,
          color: tokens.success,
          borderColor: toneColors.success.border,
          '& .MuiAlert-icon': { color: tokens.success },
        },
        standardError: {
          backgroundColor: tokens.errorTint,
          color: tokens.error,
          borderColor: toneColors.error.border,
          '& .MuiAlert-icon': { color: tokens.error },
        },
        standardWarning: {
          backgroundColor: tokens.warningTint,
          color: tokens.warning,
          borderColor: toneColors.warning.border,
          '& .MuiAlert-icon': { color: tokens.warning },
        },
        standardInfo: {
          backgroundColor: tokens.infoTint,
          color: tokens.info,
          borderColor: toneColors.info.border,
          '& .MuiAlert-icon': { color: tokens.info },
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          fontFamily: fonts.heading,
          fontWeight: 800,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: tokens.accentTint,
        },
        bar: {
          borderRadius: 4,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: tokens.divider,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontFamily: fonts.heading,
          fontWeight: 700,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontFamily: fonts.heading,
          fontWeight: 700,
          fontSize: '0.9375rem',
          minHeight: 44,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 2,
          borderRadius: 2,
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(tokens.ink, 0.06),
        },
      },
    },
  },
});

export default theme;
